import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { tryFetch } from '../../../Helpers/FetchAPI';
import '../../../styles/Home.scss';

interface Product {
  id: string;
  product_name: string;
  product_slug: string;
  unit_price: string;
  sale_price: string;
  sale_price_with_admin_fee: string;
  is_sale: boolean;
  media: { media_url: string }[];
  merchant_slug: string;
  merchant_name: string;
}

const SaleSection: React.FC = () => {
  const [saleProducts, setSaleProducts] = useState<Product[]>([]);
   const [orderedProducts, setOrderedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const currency = process.env.REACT_APP_CURRENCY;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [showSeeAll, setShowSeeAll] = useState(false);

  useEffect(() => {
    const fetchSaleProducts = async () => {
      try {
        const res = await tryFetch({
          url: '/gottsulit',
          method: 'GET',
        });

        if (!res.is_success || !Array.isArray(res.data)) {
          throw new Error('Failed to fetch GottSulit products');
        }

        const products = res.data as Product[];
        setSaleProducts(products);

        const grouped: { [key: string]: Product[] } = {};
        products.forEach((p) => {
          if (!grouped[p.merchant_slug]) grouped[p.merchant_slug] = [];
          grouped[p.merchant_slug].push(p);
        });

        const result: Product[] = [];
        let stillHas = true;
        let index = 0;
        while (stillHas) {
          stillHas = false;
          for (const merchant in grouped) {
            const slice = grouped[merchant].slice(index * 2, index * 2 + 2);
            if (slice.length > 0) {
              result.push(...slice);
              stillHas = true;
            }
          }
          index++;
        }

        setOrderedProducts(result);
      } catch (err) {
        console.error('Error fetching GottSulit products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSaleProducts();
  }, []);

  useEffect(() => {
    const checkOverflowAndCount = () => {
      if (!containerRef.current) return;

      const isOverflowing =
        containerRef.current.scrollWidth > containerRef.current.clientWidth;

      const isMobile = window.innerWidth < 768;
      const minCount = isMobile ? 2 : 5;

      setShowSeeAll(isOverflowing || saleProducts.length >= minCount);
    };

    if (!loading && saleProducts.length) {
      checkOverflowAndCount();
      window.addEventListener('resize', checkOverflowAndCount);
      return () => window.removeEventListener('resize', checkOverflowAndCount);
    }
  }, [saleProducts, loading]);

  if (!saleProducts.length && !loading) return null;

  return (
    <div className="merchant-sections">
      <div className="merchant-category">
        <div className="category-header">
          <h3>GottSulit!</h3>
          {showSeeAll && (
            <button
              className="see-all"
              onClick={() => navigate('s/a/GottSulit')}
            >
              See All
            </button>
          )}
        </div>
        <div className="merchant-list-container">
          <div className="merchant-list scrollable" ref={containerRef}>
            {loading ? (
              Array(4)
                .fill(0)
                .map((_, index) => (
                  <div key={index} className="merchant-item">
                    <div className="merchant-image-wrapper">
                      <div className="merchant-hero" />
                    </div>
                  </div>
                ))
            ) : (
              orderedProducts.map((product) => (
                <div key={product.id} className="merchant-item">
                  <Link
                    to={`/merchant/${product.merchant_slug}/product/${product.product_slug}`}
                  >
                    <div className="merchant-image-wrapper">
                      <div
                        className="merchant-hero"
                        style={{
                          backgroundImage: `url(${
                            product.media[0]?.media_url ||
                            '/default-product.jpg'
                          })`,
                        }}
                      />
                    </div>
                    <p className="product-name">
                      {product.product_name.length > 25
                        ? product.product_name.slice(0, 25) + '...'
                        : product.product_name}
                    </p>
                    </Link>
                    <p className="ss-merchant-name">
                       <Link to={`/merchant/${product.merchant_slug}`}>
                        {product.merchant_name}
                        </Link>
                      </p>
                    <div className="product-price">
                      <span className="sale-price">
                        {currency}{' '}
                        {parseFloat(product.sale_price_with_admin_fee).toFixed(2)}
                      </span>
                      <span className="old-price">
                        {currency}{' '}
                        {parseFloat(product.unit_price).toFixed(2)}
                      </span>
                    </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SaleSection;
